import { ContraPurpleLogoIcon } from '#app/__generated__/components/Icons/ContraPurpleLogo.js';
import { VerifiedGradientIcon } from '#app/__generated__/components/Icons/VerifiedGradient.js';
import { ExpertBadges } from '#app/components/ExpertBadge/ExpertBadges.js';
import { ProfileBadgeTag } from '#app/components/ProfileBadgeTag/ProfileBadgeTag.js';
import { type PossibleBadge } from '#app/hooks/useBadges.js';
import {
  EXPERT_PARTNER_USER_GROUPS,
  SupportedExpertPartner,
} from '@contra/expert-partnerships';
import { FaceMoneyEmoji } from '@contra/icons/emojis';
import { MembersOnlyIcon } from '@contra/icons/icons';

type Props = {
  readonly badges: Record<PossibleBadge, boolean>;
  readonly badgeSize?: 'default' | 'small';
  readonly categoryBadgeLabel?: null | string;
  readonly memberSinceLabel?: null | string;
  readonly memberSinceTooltip?: null | string;
  readonly oneExpertBadgeOnly?: boolean;
  readonly partnershipToHighlight?: null | SupportedExpertPartner;
  readonly platformEarningsAmountLabel?: null | string;
  readonly rateLabel?: null | string;
  readonly showFullLabel?: boolean;
  readonly source?: 'expert_list';
  readonly withColoredExpertBadges?: boolean;
};

const badgesToPartnerNames = ({
  badges,
  oneExpertBadgeOnly,
  partnershipToHighlight,
}: {
  badges: Record<PossibleBadge, boolean>;
  oneExpertBadgeOnly?: boolean;
  partnershipToHighlight?: null | SupportedExpertPartner;
}): SupportedExpertPartner[] => {
  const partnerNames: SupportedExpertPartner[] = [];

  for (const partnerName of EXPERT_PARTNER_USER_GROUPS) {
    if (badges[partnerName]) partnerNames.push(partnerName);
  }

  if (partnershipToHighlight && partnerNames.includes(partnershipToHighlight)) {
    const index = partnerNames.indexOf(partnershipToHighlight);
    partnerNames.splice(index, 1);
    partnerNames.unshift(partnershipToHighlight);
  }

  return oneExpertBadgeOnly && partnerNames.length
    ? partnerNames.slice(0, 1)
    : partnerNames;
};

export const Badges = ({
  badges,
  badgeSize = 'default',
  categoryBadgeLabel,
  memberSinceLabel,
  memberSinceTooltip,
  oneExpertBadgeOnly,
  partnershipToHighlight,
  platformEarningsAmountLabel,
  rateLabel,
  showFullLabel = false,
  source,
  withColoredExpertBadges = true,
}: Props) => {
  const partnerNames = badgesToPartnerNames({
    badges,
    oneExpertBadgeOnly,
    partnershipToHighlight,
  });

  return (
    <>
      {badges.MEMBER_SINCE && memberSinceLabel ? (
        <ProfileBadgeTag
          Icon={MembersOnlyIcon}
          label={memberSinceLabel}
          size={badgeSize}
          tooltip={memberSinceTooltip ?? undefined}
          variant="purple-gradient"
        />
      ) : null}

      <ExpertBadges
        badgeSize={badgeSize}
        partnerNames={partnerNames}
        showFullLabel={showFullLabel}
        source={source}
        withColoredExpertBadges={withColoredExpertBadges}
      />

      {badges.TOP_INDEPENDENT ? (
        <ProfileBadgeTag
          ariaLabel="Top Independent"
          Icon={ContraPurpleLogoIcon}
          // "Top Independent" does not fit on the same line with other tags when we limit the number of lines to 1
          label={showFullLabel ? 'Top Independent' : 'Top'}
          size={badgeSize}
          variant="purple"
        />
      ) : null}

      {badges.PLATFORM_EARNINGS && platformEarningsAmountLabel ? (
        <ProfileBadgeTag
          ariaLabel={`${platformEarningsAmountLabel} platform earnings`}
          Icon={FaceMoneyEmoji}
          label={
            showFullLabel
              ? `Earned ${platformEarningsAmountLabel}`
              : platformEarningsAmountLabel
          }
          size={badgeSize}
          variant="green"
        />
      ) : badges.WALLET_ACTIVE ? (
        <ProfileBadgeTag
          ariaLabel="Payments Active"
          Icon={FaceMoneyEmoji}
          label={showFullLabel ? 'Payments Active' : 'Active'}
          size={badgeSize}
          variant="green"
        />
      ) : null}

      {badges.VERIFICATION ? (
        <ProfileBadgeTag
          ariaLabel="Identity verified"
          Icon={VerifiedGradientIcon}
          label={showFullLabel ? 'Identity verified' : 'Verified'}
          size={badgeSize}
          variant="blue"
        />
      ) : null}

      {badges.CATEGORY && categoryBadgeLabel ? (
        <ProfileBadgeTag
          Icon={null}
          label={categoryBadgeLabel}
          size={badgeSize}
          variant="white"
        />
      ) : null}

      {badges.RATE && rateLabel ? (
        <ProfileBadgeTag
          Icon={null}
          label={rateLabel}
          size={badgeSize}
          variant="solid-blue"
        />
      ) : null}
    </>
  );
};
